import { useEffect, useState, useMemo } from "react";
import { Plus, ChevronDown, ChevronRight, Wrench, Trash2, Search, Pencil, FileText, Upload, ExternalLink, History, X, AlertCircle, CheckCircle, UploadCloud } from "lucide-react";
import { btn } from "@/components/common/btn";
import {
  getMasterScopes, createMasterScope, deleteMasterScope,
  getMasterEquipment, createMasterEquipment, updateMasterEquipment, deleteMasterEquipment,
  getMasterParts, createMasterPart, updateMasterPart, deleteMasterPart,
  searchMasterEquipment, getManufacturers, createManufacturer,
  getSettings, uploadPartDoc, deletePartDoc, getPartDocUrl,
  getPartInventoryLog, bulkImportParts,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { SettingsPanel, PanelHeader, PanelToolbar, EmptyState, LoadingState } from "@/components/settings/SettingsUI";

const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v);

// Inline editable cell component
function InlineEditable({ part, field, value, inlineEdit, setInlineEdit, onSave, isCurrency, suffix, badge, ...rest }) {
  const isEditing = inlineEdit.id === part.id && inlineEdit.field === field;
  if (isEditing) {
    return (
      <Input
        type="number" step="0.01" className="w-20 h-7 text-xs text-right" autoFocus
        defaultValue={value}
        onBlur={(e) => onSave(part, field, e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") onSave(part, field, e.target.value); if (e.key === "Escape") setInlineEdit({}); }}
        {...rest}
      />
    );
  }
  const display = isCurrency ? formatCurrency(value) : suffix ? `${value}${suffix}` : value;
  // `badge` colorizes low / empty on-hand counts so PMs see stock
  // problems at a glance without opening the edit modal.
  const badgeClass = badge === "empty" ? "text-slate-400" : badge === "low" ? "text-amber-600 font-semibold" : "";
  return (
    <button className={`text-sm tabular-nums hover:bg-slate-100 px-2 py-0.5 rounded cursor-pointer ${badgeClass}`} onClick={() => setInlineEdit({ id: part.id, field })} {...rest}>
      {display}
    </button>
  );
}

export default function MasterEquipmentPage({ onNavigateToManufacturers }) {
  const [scopes, setScopes] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedScopes, setExpandedScopes] = useState({});
  const [expandedEquipment, setExpandedEquipment] = useState({});

  // Dialogs
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [scopeName, setScopeName] = useState("");
  const [partDialogOpen, setPartDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [partForm, setPartForm] = useState({
    scope_id: "", equipment_id: "", new_equipment_name: "", equipment_description: "",
    part_number: "", name: "", description: "", on_hand: 0, part_cost: 0, material_margin: 50, labor_hours: 0, labor_cost: 40, labor_margin: 73.333, labor_rate: 150, freight: 0,
    use_new_equipment: false, manufacturer: "", pricing_mode: "standard", flat_rate: 0, subcontract_cost: 0,
  });

  // Manufacturers
  const [manufacturers, setManufacturers] = useState([]);
  const [newManufacturer, setNewManufacturer] = useState("");
  const [isAddingMfg, setIsAddingMfg] = useState(false);
  const [navigateToManufacturers, setNavigateToManufacturers] = useState(false);
  const [pendingPartForm, setPendingPartForm] = useState(null);

  // Inline editing
  const [inlineEdit, setInlineEdit] = useState({});
  const [appSettings, setAppSettings] = useState(null);

  // Inventory log panel inside edit dialog
  const [inventoryLog, setInventoryLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [showLog, setShowLog] = useState(false);

  // CSV Bulk Import
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvParseErrors, setCsvParseErrors] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  // Manufacturers filtered by selected scope — using manufacturer.scopes field
  const scopeManufacturers = useMemo(() => {
    if (!partForm.scope_id) return manufacturers.map(m => m.name).sort();
    return manufacturers
      .filter(m => (m.scopes || []).includes(partForm.scope_id))
      .map(m => m.name)
      .sort();
  }, [manufacturers, partForm.scope_id]);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [scopeRes, equipRes, partRes, mfgRes, settingsRes] = await Promise.all([
        getMasterScopes(), getMasterEquipment({}), getMasterParts({}), getManufacturers(), getSettings(),
      ]);
      setScopes(scopeRes.data);
      setEquipment(equipRes.data);
      setParts(partRes.data);
      setManufacturers(mfgRes.data);
      setAppSettings(settingsRes.data);
    } catch { toast.error("Failed to load equipment library"); }
    finally { setLoading(false); }
  };

  // Search filter — multi-word OR: each space-separated word is matched independently
  const filteredData = useMemo(() => {
    if (!search.trim()) return { scopes, equipment, parts, filteredScopeIds: null };
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    const matchesAny = (text) => words.some(w => text.includes(w));

    const matchedParts = parts.filter(p =>
      matchesAny((p.name || "").toLowerCase()) || matchesAny((p.part_number || "").toLowerCase()) || matchesAny((p.description || "").toLowerCase())
    );
    const matchedPartIds = new Set(matchedParts.map(p => p.id));
    const scopeIds = new Set(matchedParts.map(p => p.scope_id));
    return {
      scopes: scopes.filter(s => scopeIds.has(s.id)),
      equipment: [],
      parts: matchedParts,
      filteredScopeIds: scopeIds,
      matchedPartIds,
    };
  }, [scopes, equipment, parts, search]);

  const toggleScope = (id) => setExpandedScopes(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleEquip = (id) => setExpandedEquipment(prev => ({ ...prev, [id]: !prev[id] }));

  // Add scope
  const handleAddScope = async () => {
    if (!scopeName.trim()) return;
    try {
      await createMasterScope({ name: scopeName.trim() });
      setScopeName("");
      setScopeDialogOpen(false);
      toast.success("Scope added");
      loadAll();
    } catch { toast.error("Failed to add scope"); }
  };

  // Delete scope
  const handleDeleteScope = async (id) => {
    try { await deleteMasterScope(id); toast.success("Scope deleted"); loadAll(); }
    catch { toast.error("Failed to delete scope"); }
  };

  // Delete equipment
  const handleDeleteEquipment = async (id) => {
    try { await deleteMasterEquipment(id); toast.success("Equipment deleted"); loadAll(); }
    catch { toast.error("Failed to delete equipment"); }
  };

  // Delete part
  const handleDeletePart = async (id) => {
    try { await deleteMasterPart(id); toast.success("Part deleted"); loadAll(); }
    catch { toast.error("Failed to delete part"); }
  };

  // Open part dialog for add
  const openAddPart = (scopeId, equipmentId) => {
    setEditingPart(null);
    const labCost = appSettings?.default_labor_cost ?? 40;
    const labMargin = appSettings?.default_labor_margin ?? 73.333;
    const matMargin = appSettings?.default_material_margin ?? 50;
    const labRate = labMargin >= 100 ? labCost : Math.ceil(labCost / (1 - labMargin / 100));
    setPartForm({
      scope_id: scopeId || "", equipment_id: equipmentId || "", new_equipment_name: "", equipment_description: "",
      part_number: "", name: "", description: "", on_hand: 0, part_cost: 0, material_margin: matMargin, labor_hours: 0, labor_cost: labCost, labor_margin: labMargin, labor_rate: labRate, freight: 0,
      use_new_equipment: !equipmentId, manufacturer: "", pricing_mode: "standard", flat_rate: 0, subcontract_cost: 0,
    });
    setPartDialogOpen(true);
  };

  // Open part dialog for edit
  const openEditPart = (part) => {
    setEditingPart(part);
    setPartForm({
      scope_id: part.scope_id, equipment_id: part.equipment_id, new_equipment_name: "", equipment_description: "",
      part_number: part.part_number || "", name: part.name, description: part.description || "",
      on_hand: part.on_hand ?? 0,
      part_cost: part.part_cost || 0, material_margin: part.material_margin ?? 50, labor_hours: part.labor_hours || 0, labor_cost: part.labor_cost ?? 40, labor_margin: part.labor_margin ?? 73.333, labor_rate: part.labor_rate || 150, freight: part.freight || 0,
      use_new_equipment: false, manufacturer: part.manufacturer || "",
      pricing_mode: part.pricing_mode || "standard", flat_rate: part.flat_rate || 0,
      subcontract_cost: part.subcontract_cost || 0,
    });
    setShowLog(false);
    setInventoryLog([]);
    setPartDialogOpen(true);
  };

  const loadInventoryLog = async (partId) => {
    setLogLoading(true);
    try {
      const res = await getPartInventoryLog(partId, 20);
      setInventoryLog(res.data || []);
    } catch { setInventoryLog([]); }
    setLogLoading(false);
  };

  // Save part (create or update)
  const handleSavePart = async () => {
    if (!partForm.name.trim()) { toast.error("Part name is required"); return; }
    if (!partForm.scope_id) { toast.error("Select a scope"); return; }
    try {
      let equipId = partForm.equipment_id;
      // Auto-find or create a default equipment for this scope
      if (!equipId) {
        const scopeEquip = equipment.filter(e => e.scope_id === partForm.scope_id);
        if (scopeEquip.length > 0) {
          equipId = scopeEquip[0].id;
        } else {
          const scopeName = scopes.find(s => s.id === partForm.scope_id)?.name || "General";
          const res = await createMasterEquipment({
            name: scopeName,
            scope_id: partForm.scope_id,
            description: "",
          });
          equipId = res.data.id;
          loadAll();
        }
      }

      const payload = {
        equipment_id: equipId,
        scope_id: partForm.scope_id,
        manufacturer: partForm.manufacturer || "",
        part_number: partForm.part_number,
        name: partForm.name.trim(),
        description: partForm.description,
        on_hand: parseFloat(partForm.on_hand) || 0,
        part_cost: parseFloat(partForm.part_cost) || 0,
        material_margin: parseFloat(partForm.material_margin) || 50,
        labor_hours: parseFloat(partForm.labor_hours) || 0,
        labor_cost: parseFloat(partForm.labor_cost) || 40,
        labor_margin: parseFloat(partForm.labor_margin) || 73.333,
        labor_rate: Math.ceil((parseFloat(partForm.labor_cost) || 40) / (1 - (parseFloat(partForm.labor_margin) || 73.333) / 100)),
        freight: parseFloat(partForm.freight) || 0,
        pricing_mode: partForm.pricing_mode || "standard",
        flat_rate: parseFloat(partForm.flat_rate) || 0,
        subcontract_cost: parseFloat(partForm.subcontract_cost) || 0,
      };
      if (editingPart) {
        await updateMasterPart(editingPart.id, payload);
        toast.success("Part updated");
      } else {
        await createMasterPart(payload);
        toast.success("Part added");
      }
      setPartDialogOpen(false);
      loadAll();
    } catch { toast.error("Failed to save part"); }
  };

  // Inline save for editable fields
  const handleInlineSave = async (part, field, value) => {
    const numVal = parseFloat(value) || 0;
    if (numVal === (part[field] || 0)) { setInlineEdit({}); return; }
    const updated = { ...part, [field]: numVal };
    // Recalculate labor_rate when labor_cost or labor_margin changes (gross margin)
    if (field === "labor_cost" || field === "labor_margin") {
      const cost = field === "labor_cost" ? numVal : (part.labor_cost ?? 40);
      const gm = field === "labor_margin" ? numVal : (part.labor_margin ?? 73.333);
      updated.labor_rate = gm >= 100 ? cost : Math.ceil(cost / (1 - gm / 100));
    }
    try {
      await updateMasterPart(part.id, updated);
      setParts(prev => prev.map(p => p.id === part.id ? { ...p, ...updated } : p));
      setInlineEdit({});
    } catch { toast.error("Failed to update"); }
  };

  const parseCsvText = (raw) => {
    const lines = raw.trim().split("\n").filter(l => l.trim());
    const rows = [];
    const errs = [];
    // Expected columns: part_number, name, scope_name, on_hand, part_cost, labor_hours, manufacturer
    // OR: name, scope_name, on_hand, part_cost (simpler 4-col)
    lines.forEach((line, i) => {
      const cols = line.split("\t").map(c => c.trim());
      if (cols.length < 2) { errs.push(`Row ${i+1}: too few columns`); return; }
      // Auto-detect column count
      if (cols.length >= 7) {
        rows.push({ part_number: cols[0], name: cols[1], scope_name: cols[2], on_hand: parseFloat(cols[3])||0, part_cost: parseFloat(cols[4])||0, labor_hours: parseFloat(cols[5])||0, manufacturer: cols[6] });
      } else if (cols.length >= 5) {
        rows.push({ part_number: cols[0], name: cols[1], scope_name: cols[2], on_hand: parseFloat(cols[3])||0, part_cost: parseFloat(cols[4])||0, labor_hours: 0, manufacturer: "" });
      } else if (cols.length >= 4) {
        rows.push({ part_number: "", name: cols[0], scope_name: cols[1], on_hand: parseFloat(cols[2])||0, part_cost: parseFloat(cols[3])||0, labor_hours: 0, manufacturer: "" });
      } else {
        rows.push({ part_number: "", name: cols[0], scope_name: cols[1] || "", on_hand: 0, part_cost: 0, labor_hours: 0, manufacturer: "" });
      }
    });
    setCsvPreview(rows);
    setCsvParseErrors(errs);
    setImportResult(null);
  };

  const handleCsvImport = async (upsert = false) => {
    if (!csvPreview.length) { toast.error("No rows to import"); return; }
    setImporting(true);
    try {
      const res = await bulkImportParts({ rows: csvPreview, upsert });
      const d = res.data;
      setImportResult(d);
      if (d.inserted > 0 || d.updated > 0) {
        toast.success(`Imported: ${d.inserted} new, ${d.updated} updated, ${d.skipped} skipped`);
        loadAll();
      } else {
        toast.warning(`Nothing imported — ${d.skipped} skipped. Check errors.`);
      }
    } catch { toast.error("Import failed"); }
    setImporting(false);
  };

  const scopeEquipment = (scopeId) => filteredData.equipment.filter(e => e.scope_id === scopeId).sort((a, b) => a.name.localeCompare(b.name));
  const equipParts = (equipId) => filteredData.parts.filter(p => p.equipment_id === equipId).sort((a, b) => a.name.localeCompare(b.name));
  const displayScopes = [...filteredData.scopes].sort((a, b) => a.name.localeCompare(b.name));

  // Auto-expand when searching
  useEffect(() => {
    if (search.trim()) {
      const se = {}, ee = {};
      displayScopes.forEach(s => { se[s.id] = true; });
      filteredData.equipment.forEach(e => { ee[e.id] = true; });
      setExpandedScopes(se);
      setExpandedEquipment(ee);
    }
    // Only re-run when the search term or scope count changes; reading
    // the full arrays in deps would re-expand every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, displayScopes.length]);

  return (
    <div data-testid="master-equipment-page">
      <SettingsPanel>
        <PanelHeader
          icon={Wrench}
          title="Equipment Library"
          count={displayScopes.length || null}
          hint="Scopes and the parts inside them"
        />
        <PanelToolbar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search equipment or parts…"
          searchTestId="search-equipment"
          actions={
            <>
              <button type="button" className={btn("write")} onClick={() => setScopeDialogOpen(true)} data-testid="btn-add-scope">
                <Plus className="w-3.5 h-3.5" /> Scope
              </button>
              <button type="button" className={btn("write")} onClick={() => openAddPart("", "")} data-testid="btn-add-part">
                <Plus className="w-3.5 h-3.5" /> Part
              </button>
              <button type="button" className={btn("data")} onClick={() => { setCsvDialogOpen(true); setCsvText(""); setCsvPreview([]); setCsvParseErrors([]); setImportResult(null); }} data-testid="btn-bulk-import">
                <Upload className="w-3.5 h-3.5" /> Bulk Import
              </button>
            </>
          }
        />

        {loading ? (
          <LoadingState />
        ) : displayScopes.length === 0 ? (
          <EmptyState icon={Wrench} title="No equipment found" hint="Add a scope to start building your equipment library." />
        ) : (
          <div className="divide-y">
            {displayScopes.map((scope) => (
              <div key={scope.id} data-testid={`scope-${scope.id}`}>
                {/* Scope header — iter170 hygiene: switched from
                    <button> wrapping a child Button to a clickable
                    <div role="button"> so we don't nest two buttons
                    (React hydration warning). Keyboard handlers
                    preserve Tab/Enter/Space toggle support. */}
                <div
                  role="button"
                  tabIndex={0}
                  className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => toggleScope(scope.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleScope(scope.id); } }}
                  data-testid={`toggle-scope-${scope.id}`}
                >
                  {expandedScopes[scope.id] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  <span className="text-sm font-medium text-slate-800">{scope.name}</span>
                  <span className="text-[10px] text-slate-400">({(search.trim() ? filteredData.parts : parts).filter(p => p.scope_id === scope.id).length} parts{search.trim() ? " matching" : ""})</span>
                  <div className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openAddPart(scope.id, "")} data-testid={`add-part-scope-${scope.id}`}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add
                    </Button>
                  </div>
                </div>

                {/* Parts directly under scope — flat list */}
                {expandedScopes[scope.id] && (() => {
                  const scopeParts = (search.trim() ? filteredData.parts : parts)
                    .filter(p => p.scope_id === scope.id)
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                  return (
                  <div className="pl-6 pr-2 pb-2">
                    {scopeParts.length === 0 ? (
                      <p className="text-sm text-slate-400 py-2 pl-4">No parts in this scope</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs font-semibold">Name</TableHead>
                            <TableHead className="text-xs font-semibold">Part #</TableHead>
                            <TableHead className="text-xs font-semibold text-right">On Hand</TableHead>
                            <TableHead className="text-xs font-semibold text-right">Mat Cost</TableHead>
                            <TableHead className="text-xs font-semibold text-right">GM%</TableHead>
                            <TableHead className="text-xs font-semibold text-right">Total Mat</TableHead>
                            <TableHead className="text-xs font-semibold text-right">Lab Cost</TableHead>
                            <TableHead className="text-xs font-semibold text-right">GM%</TableHead>
                            <TableHead className="text-xs font-semibold text-right">$/Hr</TableHead>
                            <TableHead className="text-xs font-semibold text-right">Hours</TableHead>
                            <TableHead className="text-xs font-semibold text-right">Total Labor</TableHead>
                            <TableHead className="text-xs font-semibold text-right bg-slate-50">Total</TableHead>
                            <TableHead className="text-xs font-semibold text-right bg-slate-50">GM%</TableHead>
                            <TableHead className="w-16"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {scopeParts.map((part) => {
                            const isFlat = part.pricing_mode === "flat_rate";
                            const isSub = part.pricing_mode === "subcontract";
                            const matGM = part.material_margin ?? 50;
                            const totalMat = matGM >= 100 ? (part.part_cost || 0) : (part.part_cost || 0) / (1 - matGM / 100);
                            const labCost = part.labor_cost ?? 40;
                            const labGM = part.labor_margin ?? 73.333;
                            const ratePerHr = labGM >= 100 ? labCost : Math.ceil(labCost / (1 - labGM / 100));
                            const totalLabor = (part.labor_hours || 0) * ratePerHr;
                            const flatRate = parseFloat(part.flat_rate) || 0;
                            const grandTotal = isFlat ? flatRate : totalMat + totalLabor;
                            const totalCost = isFlat ? (part.labor_hours || 0) * labCost : (part.part_cost || 0) + (part.labor_hours || 0) * labCost;
                            const overallGM = grandTotal > 0 ? ((grandTotal - totalCost) / grandTotal * 100) : 0;
                            if (isSub) {
                              const price = parseFloat(part.flat_rate) || 0;
                              const subCost = parseFloat(part.subcontract_cost) || 0;
                              const subGM = price > 0 ? ((price - subCost) / price) * 100 : 0;
                              return (
                                <TableRow key={part.id} className="group bg-sky-50/30" data-testid={`part-row-${part.id}`}>
                                  <TableCell>
                                    <button className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer" onClick={() => openEditPart(part)} data-testid={`part-name-${part.id}`}>
                                      {part.name}
                                    </button>
                                  </TableCell>
                                  <TableCell className="text-xs text-slate-500 font-mono">{part.part_number || "-"}</TableCell>
                                  <TableCell className="text-right text-xs text-slate-300">—</TableCell>
                                  <TableCell className="text-right">
                                    <span className="text-[9px] font-bold text-sky-700 bg-sky-100 border border-sky-200 rounded px-1.5 py-0.5" data-testid={`part-sub-badge-${part.id}`}>SUB</span>
                                  </TableCell>
                                  <TableCell className="text-right text-xs text-slate-300">—</TableCell>
                                  <TableCell className="text-right text-xs text-slate-300">—</TableCell>
                                  <TableCell className="text-right text-xs text-slate-300">—</TableCell>
                                  <TableCell className="text-right text-xs text-slate-300">—</TableCell>
                                  <TableCell className="text-right text-xs text-slate-300">—</TableCell>
                                  <TableCell className="text-right text-xs text-slate-300">—</TableCell>
                                  <TableCell className="text-right">
                                    <InlineEditable part={part} field="subcontract_cost" value={subCost} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} onSave={handleInlineSave} isCurrency data-testid={`part-sub-cost-${part.id}`} />
                                    <span className="text-[9px] text-slate-400 ml-1">sub</span>
                                  </TableCell>
                                  <TableCell className="text-right bg-sky-50/60">
                                    <InlineEditable part={part} field="flat_rate" value={price} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} onSave={handleInlineSave} isCurrency data-testid={`part-sub-price-${part.id}`} />
                                  </TableCell>
                                  <TableCell className={`text-sm text-right tabular-nums font-semibold bg-slate-50 ${subGM >= 30 ? "text-green-600" : "text-amber-600"}`}>{subGM.toFixed(1)}%</TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <button className="p-1 hover:bg-slate-100 rounded" onClick={() => openEditPart(part)} data-testid={`edit-part-${part.id}`}>
                                        <Pencil className="w-3.5 h-3.5 text-slate-400" />
                                      </button>
                                      <button className="p-1 hover:bg-red-50 rounded" onClick={() => handleDeletePart(part.id)} data-testid={`delete-part-${part.id}`}>
                                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                      </button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            }
                            if (isFlat) {
                              return (
                              <TableRow key={part.id} className="group bg-amber-50/30" data-testid={`part-row-${part.id}`}>
                                <TableCell>
                                  <button className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer" onClick={() => openEditPart(part)} data-testid={`part-name-${part.id}`}>
                                    {part.name}
                                  </button>
                                </TableCell>
                                <TableCell className="text-xs text-slate-500 font-mono">{part.part_number || "-"}</TableCell>
                                <TableCell className="text-right text-xs text-slate-300">—</TableCell>
                                <TableCell className="text-right">
                                  <span className="text-[9px] font-bold text-amber-600 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`part-flat-badge-${part.id}`}>FLAT</span>
                                </TableCell>
                                <TableCell className="text-right text-xs text-slate-300">—</TableCell>
                                <TableCell className="text-right text-xs text-slate-300">—</TableCell>
                                <TableCell className="text-right">
                                  <InlineEditable part={part} field="labor_cost" value={labCost} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} onSave={handleInlineSave} isCurrency />
                                </TableCell>
                                <TableCell className="text-right text-xs text-slate-300">—</TableCell>
                                <TableCell className="text-right text-xs text-slate-300">—</TableCell>
                                <TableCell className="text-right">
                                  <InlineEditable part={part} field="labor_hours" value={part.labor_hours || 0} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} onSave={handleInlineSave} />
                                </TableCell>
                                <TableCell className="text-sm text-right tabular-nums text-slate-500">{formatCurrency(totalCost)} <span className="text-[9px] text-slate-400">cost</span></TableCell>
                                <TableCell className="text-right bg-amber-50/60">
                                  <InlineEditable part={part} field="flat_rate" value={flatRate} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} onSave={handleInlineSave} isCurrency data-testid={`part-flat-rate-${part.id}`} />
                                </TableCell>
                                <TableCell className={`text-sm text-right tabular-nums font-semibold bg-slate-50 ${overallGM >= 30 ? "text-green-600" : "text-red-500"}`}>{overallGM.toFixed(1)}%</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button className="p-1 hover:bg-slate-100 rounded" onClick={() => openEditPart(part)} data-testid={`edit-part-${part.id}`}>
                                      <Pencil className="w-3.5 h-3.5 text-slate-400" />
                                    </button>
                                    <button className="p-1 hover:bg-red-50 rounded" onClick={() => handleDeletePart(part.id)} data-testid={`delete-part-${part.id}`}>
                                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                    </button>
                                  </div>
                                </TableCell>
                              </TableRow>
                              );
                            }
                            return (
                            <TableRow key={part.id} className="group" data-testid={`part-row-${part.id}`}>
                              <TableCell>
                                <button className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer" onClick={() => openEditPart(part)} data-testid={`part-name-${part.id}`}>
                                  {part.name}
                                </button>
                              </TableCell>
                              <TableCell className="text-xs text-slate-500 font-mono">{part.part_number || "-"}</TableCell>
                              <TableCell className="text-right">
                                <InlineEditable part={part} field="on_hand" value={part.on_hand ?? 0} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} onSave={handleInlineSave} badge={(part.on_hand ?? 0) <= 0 ? "empty" : ((part.on_hand ?? 0) <= 3 ? "low" : "")} data-testid={`part-on-hand-${part.id}`} />
                              </TableCell>
                              <TableCell className="text-right relative">
                                <InlineEditable part={part} field="part_cost" value={part.part_cost} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} onSave={handleInlineSave} isCurrency />
                                {part.last_price_updated && (
                                  <span
                                    className="absolute right-2 bottom-0.5 text-[9px] text-slate-400 tabular-nums leading-none whitespace-nowrap pointer-events-none"
                                    data-testid={`part-last-price-${part.id}`}
                                  >
                                    {new Date(part.last_price_updated).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <InlineEditable part={part} field="material_margin" value={matGM} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} onSave={handleInlineSave} suffix="%" />
                              </TableCell>
                              <TableCell className="text-sm text-right tabular-nums font-medium">{formatCurrency(totalMat)}</TableCell>
                              <TableCell className="text-right">
                                <InlineEditable part={part} field="labor_cost" value={labCost} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} onSave={handleInlineSave} isCurrency />
                              </TableCell>
                              <TableCell className="text-right">
                                <InlineEditable part={part} field="labor_margin" value={labGM} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} onSave={handleInlineSave} suffix="%" />
                              </TableCell>
                              <TableCell className="text-sm text-right tabular-nums">{formatCurrency(ratePerHr)}</TableCell>
                              <TableCell className="text-right">
                                <InlineEditable part={part} field="labor_hours" value={part.labor_hours || 0} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} onSave={handleInlineSave} />
                              </TableCell>
                              <TableCell className="text-sm text-right tabular-nums font-medium">{formatCurrency(totalLabor)}</TableCell>
                              <TableCell className="text-sm text-right tabular-nums font-bold bg-slate-50">{formatCurrency(grandTotal)}</TableCell>
                              <TableCell className={`text-sm text-right tabular-nums font-semibold bg-slate-50 ${overallGM >= 30 ? "text-green-600" : "text-red-500"}`}>{overallGM.toFixed(1)}%</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button className="p-1 hover:bg-slate-100 rounded" onClick={() => openEditPart(part)} data-testid={`edit-part-${part.id}`}>
                                    <Pencil className="w-3.5 h-3.5 text-slate-400" />
                                  </button>
                                  <button className="p-1 hover:bg-red-50 rounded" onClick={() => handleDeletePart(part.id)} data-testid={`delete-part-${part.id}`}>
                                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                  </button>
                                </div>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </SettingsPanel>

      {/* Add Scope Dialog */}
      <Dialog open={scopeDialogOpen} onOpenChange={setScopeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Add Scope</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Scope Name *</Label>
              <Input value={scopeName} onChange={(e) => setScopeName(e.target.value)} placeholder="e.g. HVAC, Bleachers, Fire Safety" data-testid="input-scope-name"
                onKeyDown={(e) => { if (e.key === "Enter") handleAddScope(); }}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setScopeDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddScope} data-testid="btn-save-scope">Save Scope</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Part Dialog */}
      <Dialog open={partDialogOpen} onOpenChange={setPartDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingPart ? "Edit Part" : "Add Part"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Row 1: Scope + Manufacturer side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Scope *</Label>
                <Select value={partForm.scope_id} onValueChange={(v) => setPartForm(f => ({ ...f, scope_id: v, equipment_id: "", manufacturer: "" }))}>
                  <SelectTrigger data-testid="select-part-scope"><SelectValue placeholder="Select scope" /></SelectTrigger>
                  <SelectContent>
                    {scopes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Vendor</Label>
                <div className="flex gap-2">
                  <Select value={partForm.manufacturer || "none"} onValueChange={(v) => setPartForm(f => ({ ...f, manufacturer: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="select-part-manufacturer"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {scopeManufacturers.map(name => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button type="button" size="sm" variant="outline" onClick={() => {
                    if (onNavigateToManufacturers) {
                      onNavigateToManufacturers({ partForm, editingPart });
                    }
                  }} title="Add new vendor" data-testid="btn-navigate-add-manufacturer">
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Row 2: Part Name · Part Number · On Hand (auto-deducted
                when a WO is first dispatched or a parts-counter order
                ships; PMs can also edit here for cycle counts). */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Part Name *</Label>
                <Input value={partForm.name} onChange={(e) => setPartForm(f => ({ ...f, name: e.target.value }))} data-testid="input-part-name" />
              </div>
              <div>
                <Label className="text-xs">Part Number</Label>
                <Input value={partForm.part_number} onChange={(e) => setPartForm(f => ({ ...f, part_number: e.target.value }))} placeholder="Optional" className="placeholder:text-slate-400" data-testid="input-part-number" />
              </div>
              <div>
                <Label className="text-xs flex items-center justify-between">
                  <span>On Hand</span>
                  <span className="text-[10px] font-normal text-slate-400">auto-deducts</span>
                </Label>
                <Input type="number" step="0.01" value={partForm.on_hand} onChange={(e) => setPartForm(f => ({ ...f, on_hand: e.target.value }))} data-testid="input-part-on-hand" />
              </div>
            </div>

            {/* Row 3: Description */}
            <div>
              <Label className="text-xs">Part Description</Label>
              <textarea
                value={partForm.description}
                onChange={(e) => setPartForm(f => ({ ...f, description: e.target.value }))}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[60px] resize-y"
                placeholder="Optional"
                data-testid="input-part-description"
              />
            </div>

            {/* === PRICING MODE toggle (iter235) ================= */}
            <div className="flex flex-wrap items-center gap-3 px-3 py-2 border border-amber-200 bg-amber-50/40 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={partForm.pricing_mode === "flat_rate"}
                  onChange={(e) => setPartForm(f => ({ ...f, pricing_mode: e.target.checked ? "flat_rate" : "standard" }))}
                  className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  data-testid="toggle-flat-rate-pricing"
                />
                <span className="text-xs font-semibold text-amber-700">Flat Rate pricing (service)</span>
              </label>
              <span className="text-[10px] text-slate-500">Unit price is entered directly — hours × labor cost back into the margin.</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={partForm.pricing_mode === "subcontract"}
                  onChange={(e) => setPartForm(f => ({ ...f, pricing_mode: e.target.checked ? "subcontract" : "standard" }))}
                  className="h-4 w-4 rounded border-sky-300 text-sky-600 focus:ring-sky-500"
                  data-testid="toggle-subcontract-pricing"
                />
                <span className="text-xs font-semibold text-sky-700">Subcontract pass-through</span>
              </label>
              <span className="text-[10px] text-slate-500">Sub cost and customer price are both entered — no margin math (Window Care).</span>
            </div>

            {partForm.pricing_mode === "subcontract" ? (
              <div className="border border-sky-200 rounded-lg bg-sky-50/40 p-3 space-y-2" data-testid="subcontract-section">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold tracking-wider text-sky-700 uppercase">Subcontract pass-through</span>
                  {(() => {
                    const price = parseFloat(partForm.flat_rate) || 0;
                    const cost = parseFloat(partForm.subcontract_cost) || 0;
                    const gm = price > 0 ? ((price - cost) / price) * 100 : 0;
                    return (
                      <div className="flex items-center gap-2 px-2.5 py-1 bg-white border border-sky-200 rounded-md">
                        <span className="text-[11px] uppercase tracking-wide text-slate-500">Margin</span>
                        <span className={`text-sm font-bold tabular-nums ${gm >= 30 ? "text-green-600" : "text-amber-600"}`} data-testid="subcontract-margin-display">{gm.toFixed(1)}%</span>
                        <span className="text-[11px] text-slate-400">({formatCurrency(price - cost)} / unit)</span>
                      </div>
                    );
                  })()}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Sub cost ($/unit)</Label>
                    <Input type="number" step="0.01" value={partForm.subcontract_cost || ""} onChange={(e) => setPartForm(f => ({ ...f, subcontract_cost: e.target.value }))} className="h-9 text-sm bg-white" data-testid="input-subcontract-cost" />
                  </div>
                  <div>
                    <Label className="text-xs">Customer price ($/unit)</Label>
                    <Input type="number" step="0.01" value={partForm.flat_rate || ""} onChange={(e) => setPartForm(f => ({ ...f, flat_rate: e.target.value }))} className="h-9 text-sm bg-white" data-testid="input-subcontract-price" />
                  </div>
                </div>
              </div>
            ) : partForm.pricing_mode === "flat_rate" ? (
              <div className="border border-amber-200 rounded-lg bg-amber-50/40 p-3 space-y-2" data-testid="flat-rate-section">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold tracking-wider text-amber-700 uppercase">Flat Rate</span>
                  {(() => {
                    const fr = parseFloat(partForm.flat_rate) || 0;
                    const cost = (parseFloat(partForm.labor_hours) || 0) * (parseFloat(partForm.labor_cost) || 0);
                    const gm = fr > 0 ? ((fr - cost) / fr) * 100 : 0;
                    return (
                      <div className="flex items-center gap-2 px-2.5 py-1 bg-white border border-amber-200 rounded-md">
                        <span className="text-[11px] uppercase tracking-wide text-slate-500">Margin</span>
                        <span className={`text-sm font-bold tabular-nums ${gm >= 30 ? "text-green-600" : "text-red-500"}`} data-testid="flat-rate-margin-display">{gm.toFixed(1)}%</span>
                        <span className="text-[11px] text-slate-400">({formatCurrency(fr - cost)} / unit)</span>
                      </div>
                    );
                  })()}
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Flat Rate ($/unit)</Label>
                    <Input type="number" step="0.01" value={partForm.flat_rate || ""} onChange={(e) => setPartForm(f => ({ ...f, flat_rate: e.target.value }))} className="h-9 text-sm bg-white" data-testid="input-flat-rate" />
                  </div>
                  <div>
                    <Label className="text-xs">Hours / unit</Label>
                    <Input type="number" step="0.25" value={partForm.labor_hours || ""} onChange={(e) => setPartForm(f => ({ ...f, labor_hours: e.target.value }))} className="h-9 text-sm bg-white" data-testid="input-flat-hours" />
                  </div>
                  <div>
                    <Label className="text-xs">Lab Cost ($/hr)</Label>
                    <Input type="number" step="0.5" value={partForm.labor_cost || ""} onChange={(e) => setPartForm(f => ({ ...f, labor_cost: e.target.value }))} className="h-9 text-sm bg-white" data-testid="input-flat-labor-cost" />
                  </div>
                  <div>
                    <Label className="text-xs">Cost / unit</Label>
                    <div className="h-9 flex items-center px-2 bg-slate-100 border border-slate-200 rounded-md text-sm font-medium tabular-nums">
                      {formatCurrency((parseFloat(partForm.labor_hours) || 0) * (parseFloat(partForm.labor_cost) || 0))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (<>
            {/* === MATERIAL section ============================
                Same grouped-section pattern as Customer > Equipment edit
                (Clients.jsx). Library-level only — Other (Equipment/
                Disposal/Travel) is intentionally excluded; those buckets
                are configured per-customer when the part is added to a
                specific equipment record. */}
            <div className="border border-slate-200 rounded-lg bg-slate-50/60 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold tracking-wider text-slate-500 uppercase">Material</span>
                <div className="flex items-center gap-2 px-2.5 py-1 bg-white border border-slate-200 rounded-md">
                  <span className="text-[11px] uppercase tracking-wide text-slate-500">Total Material</span>
                  <span className="text-sm font-bold tabular-nums text-slate-900">
                    {(() => { const c = parseFloat(partForm.part_cost) || 0; const gm = parseFloat(partForm.material_margin) || 0; return formatCurrency(gm >= 100 ? c : c / (1 - gm / 100)); })()}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Material Cost</Label>
                  <Input type="number" step="0.01" value={partForm.part_cost || ""} onChange={(e) => setPartForm(f => ({ ...f, part_cost: e.target.value }))} className="h-9 text-sm bg-white" data-testid="input-part-cost" />
                  <span className="text-[10px] text-slate-500 tabular-nums" data-testid="last-price-updated">
                    Last updated:{" "}
                    {editingPart?.last_price_updated
                      ? new Date(editingPart.last_price_updated).toLocaleDateString("en-US", { month: "long", year: "numeric" })
                      : "Never"}
                  </span>
                </div>
                <div>
                  <Label className="text-xs">Mat GM%</Label>
                  <Input type="number" step="1" value={Math.round(parseFloat(partForm.material_margin) || 0)} onChange={(e) => setPartForm(f => ({ ...f, material_margin: e.target.value }))} className="h-9 text-sm bg-white" data-testid="input-material-margin" />
                </div>
                <div>
                  <Label className="text-xs">Freight</Label>
                  <Input type="number" step="0.01" value={partForm.freight || ""} onChange={(e) => setPartForm(f => ({ ...f, freight: e.target.value }))} className="h-9 text-sm bg-white" data-testid="input-freight" />
                </div>
              </div>
            </div>

            {/* === LABOR section ================================ */}
            <div className="border border-slate-200 rounded-lg bg-slate-50/60 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold tracking-wider text-slate-500 uppercase">Labor</span>
                <div className="flex items-center gap-2 px-2.5 py-1 bg-white border border-slate-200 rounded-md">
                  <span className="text-[11px] uppercase tracking-wide text-slate-500">Total Labor</span>
                  <span className="text-sm font-bold tabular-nums text-slate-900">
                    {(() => { const c = parseFloat(partForm.labor_cost) || 0; const gm = parseFloat(partForm.labor_margin) || 0; const rate = gm >= 100 ? c : Math.ceil(c / (1 - gm / 100)); return formatCurrency((parseFloat(partForm.labor_hours) || 0) * rate); })()}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Label className="text-xs">Lab Cost ($/hr)</Label>
                  <Input type="number" step="0.5" value={partForm.labor_cost || ""} onChange={(e) => setPartForm(f => ({ ...f, labor_cost: e.target.value }))} className="h-9 text-sm bg-white" data-testid="input-labor-cost" />
                </div>
                <div>
                  <Label className="text-xs">Lab GM%</Label>
                  <Input type="number" step="1" value={Math.round(parseFloat(partForm.labor_margin) || 0)} onChange={(e) => setPartForm(f => ({ ...f, labor_margin: e.target.value }))} className="h-9 text-sm bg-white" data-testid="input-labor-margin" />
                </div>
                <div>
                  <Label className="text-xs">$/Hr (Rate)</Label>
                  <div className="h-9 flex items-center px-2 bg-slate-100 border border-slate-200 rounded-md text-sm font-medium tabular-nums">
                    {(() => { const c = parseFloat(partForm.labor_cost) || 0; const gm = parseFloat(partForm.labor_margin) || 0; return formatCurrency(gm >= 100 ? c : Math.ceil(c / (1 - gm / 100))); })()}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Hours</Label>
                  <Input type="number" step="0.5" value={partForm.labor_hours || ""} onChange={(e) => setPartForm(f => ({ ...f, labor_hours: e.target.value }))} className="h-9 text-sm bg-white" data-testid="input-labor-hours" />
                </div>
              </div>
            </div>
            </>)}

            {/* === INVENTORY LOG (edit mode only) =================== */}
            {editingPart && (
              <div className="border-t pt-3 mt-2">
                <button
                  className="flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-slate-800 transition-colors"
                  onClick={() => {
                    if (!showLog) loadInventoryLog(editingPart.id);
                    setShowLog(v => !v);
                  }}
                  data-testid="btn-toggle-inventory-log"
                >
                  <History className="w-3.5 h-3.5 text-violet-500" />
                  Inventory Movement Log
                  <span className="ml-auto text-[10px] font-normal text-slate-400">{showLog ? "hide" : "show last 20"}</span>
                </button>
                {showLog && (
                  <div className="mt-2 space-y-0.5 max-h-52 overflow-y-auto" data-testid="inventory-log-panel">
                    {logLoading ? (
                      <div className="text-xs text-slate-400 text-center py-4">Loading…</div>
                    ) : inventoryLog.length === 0 ? (
                      <div className="text-xs text-slate-400 text-center py-4">No movements recorded yet.</div>
                    ) : inventoryLog.map((row, i) => {
                      const delta = Number(row.delta);
                      const isPos = delta > 0;
                      const at = row.at ? new Date(row.at).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"2-digit", hour:"2-digit", minute:"2-digit" }) : "—";
                      return (
                        <div key={row.id || i} className="flex items-center gap-2 px-2 py-1 rounded text-xs bg-slate-50 border border-slate-100" data-testid={`log-row-${i}`}>
                          <span className={`w-14 text-right font-mono font-semibold tabular-nums ${isPos ? "text-emerald-600" : "text-red-500"}`}>
                            {isPos ? "+" : ""}{delta}
                          </span>
                          <span className="flex-1 text-slate-600 truncate">{row.source_label || row.source_type}</span>
                          {row.new_on_hand != null && (
                            <span className="text-slate-400 tabular-nums">→ {row.new_on_hand}</span>
                          )}
                          <span className="text-slate-400 whitespace-nowrap">{at}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Part Data — Tech Documents */}
            {editingPart && (
              <div className="border-t pt-3 mt-2">
                <Label className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><FileText className="w-3.5 h-3.5 text-blue-500" />Part Data (Tech Docs)</Label>
                <p className="text-[10px] text-slate-400 mb-2">Shop drawings, install instructions, tech data. Auto-included in WO Tech Info when this part is used.</p>
                {(editingPart.tech_docs || []).length > 0 && (
                  <div className="space-y-1 mb-2">
                    {(editingPart.tech_docs || []).map(doc => (
                      <div key={doc.id} className="flex items-center gap-2 p-1.5 border rounded text-xs bg-blue-50/30">
                        <FileText className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                        <span className="flex-1 truncate font-medium text-slate-700">{doc.filename}</span>
                        <a href={getPartDocUrl(editingPart.id, doc.id)} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm" className="h-5 text-[9px] px-1.5"><ExternalLink className="w-2.5 h-2.5" /></Button>
                        </a>
                        <Button variant="ghost" size="sm" className="h-5 text-[9px] px-1.5 text-red-400 hover:text-red-600" onClick={async () => {
                          try { await deletePartDoc(editingPart.id, doc.id); toast.success(`Deleted ${doc.filename}`); loadAll(); } catch { toast.error("Delete failed"); }
                        }} data-testid={`delete-part-doc-${doc.id}`}><Trash2 className="w-2.5 h-2.5" /></Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-2 border-dashed rounded p-2 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/20 transition-all"
                  onClick={() => document.getElementById(`part-doc-upload-${editingPart.id}`)?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={async (e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (!file) return;
                    const fd = new FormData(); fd.append("file", file);
                    try { await uploadPartDoc(editingPart.id, fd); toast.success(`Uploaded ${file.name}`); loadAll(); } catch { toast.error("Upload failed"); }
                  }}
                  data-testid="part-data-dropzone"
                >
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" id={`part-doc-upload-${editingPart.id}`}
                    onChange={async (e) => {
                      const file = e.target.files[0]; if (!file) return; e.target.value = "";
                      const fd = new FormData(); fd.append("file", file);
                      try { await uploadPartDoc(editingPart.id, fd); toast.success(`Uploaded ${file.name}`); loadAll(); } catch { toast.error("Upload failed"); }
                    }} />
                  <div className="flex items-center justify-center gap-1.5">
                    <Upload className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-[10px] text-slate-500">Drop files or click to upload</span>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-sm">
              {(() => {
                if (partForm.pricing_mode === "subcontract") {
                  const price = parseFloat(partForm.flat_rate) || 0;
                  const cost = parseFloat(partForm.subcontract_cost) || 0;
                  const gm = price > 0 ? ((price - cost) / price) * 100 : 0;
                  return (
                    <>
                      <div className="flex justify-between text-slate-500">
                        <span>Sub cost / unit</span>
                        <span className="tabular-nums">{formatCurrency(cost)}</span>
                      </div>
                      <div className="border-t border-slate-200 pt-1.5 mt-1.5 flex justify-between items-center">
                        <span className="font-medium text-slate-700">Customer price / unit</span>
                        <div className="flex items-center gap-3">
                          <span className="text-slate-500 text-xs tabular-nums">GM: {gm.toFixed(1)}%</span>
                          <span className="font-bold text-base tabular-nums">{formatCurrency(price)}</span>
                        </div>
                      </div>
                    </>
                  );
                }
                const matC = parseFloat(partForm.part_cost) || 0;
                const matGM = parseFloat(partForm.material_margin) || 0;
                const totalMat = matGM >= 100 ? matC : matC / (1 - matGM / 100);
                const labC = parseFloat(partForm.labor_cost) || 0;
                const labGM = parseFloat(partForm.labor_margin) || 0;
                const rate = labGM >= 100 ? labC : Math.ceil(labC / (1 - labGM / 100));
                const totalLab = (parseFloat(partForm.labor_hours) || 0) * rate;
                const freight = parseFloat(partForm.freight) || 0;
                const grand = totalMat + freight + totalLab;
                const totalCost = matC + freight + (parseFloat(partForm.labor_hours) || 0) * labC;
                const gm = grand > 0 ? ((grand - totalCost) / grand * 100) : 0;
                return (
                  <>
                    <div className="flex justify-between text-slate-500">
                      <span>Total Material</span>
                      <span className="tabular-nums">{formatCurrency(totalMat)}</span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span>Total Freight</span>
                      <span className="tabular-nums">{formatCurrency(freight)}</span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span>Total Labor</span>
                      <span className="tabular-nums">{formatCurrency(totalLab)}</span>
                    </div>
                    <div className="border-t border-slate-200 pt-1.5 mt-1.5 flex justify-between items-center">
                      <span className="font-medium text-slate-700">Total</span>
                      <div className="flex items-center gap-3">
                        <span className="text-slate-500 text-xs tabular-nums">GM: {gm.toFixed(1)}%</span>
                        <span className="font-bold text-base tabular-nums">{formatCurrency(grand)}</span>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setPartDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSavePart} data-testid="btn-save-part">
                {editingPart ? "Update" : "Add"} Part
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Bulk CSV Import Dialog */}
      <Dialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
              <UploadCloud className="w-5 h-5 text-violet-500" />
              Bulk Import Parts
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Format hint */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1">
              <p className="font-semibold text-slate-700">Paste tab-separated data from Excel (any of these formats):</p>
              <p className="font-mono text-[10px] bg-white border rounded p-1.5 text-slate-500">
                part_number  name  scope_name  on_hand  unit_cost  labor_hrs  manufacturer<br/>
                — OR —<br/>
                part_number  name  scope_name  on_hand  unit_cost<br/>
                — OR —<br/>
                name  scope_name  on_hand  unit_cost
              </p>
              <p className="text-[10px] text-slate-400">Scope name must exactly match an existing scope (e.g. "Operable Partitions", "Gym Equipment"). Upsert mode updates existing parts with the same part# + scope.</p>
            </div>

            {/* Paste area */}
            <div>
              <Label className="text-xs font-semibold">Paste Data</Label>
              <textarea
                className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-mono ring-offset-background placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[140px] resize-y"
                placeholder={"21-016\tBulbseal Rubber Brown\tOperable Partitions\t698\t2.18\t0.25\t"}
                value={csvText}
                onChange={(e) => { setCsvText(e.target.value); parseCsvText(e.target.value); }}
                data-testid="csv-paste-area"
              />
            </div>

            {/* Parse errors */}
            {csvParseErrors.length > 0 && (
              <div className="space-y-1">
                {csvParseErrors.map((e, i) => (
                  // eslint-disable-next-line react/no-array-index-key -- transient CSV parse errors; no stable id
                  <div key={`pe-${i}`} className="flex items-center gap-2 text-xs text-red-600 bg-red-50 rounded px-2 py-1">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />{e}
                  </div>
                ))}
              </div>
            )}

            {/* Preview table */}
            {csvPreview.length > 0 && !importResult && (
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-2">{csvPreview.length} rows parsed — preview:</p>
                <div className="overflow-auto max-h-52 border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        {["Part #","Name","Scope","On Hand","Cost","Labor Hrs","Vendor"].map(h => (
                          <th key={h} className="text-left px-2 py-1 font-semibold text-slate-500 border-b">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.map((row, i) => (
                        // eslint-disable-next-line react/no-array-index-key -- CSV preview rows; transient, no id yet
                        <tr key={`csv-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-2 py-1 font-mono text-slate-600">{row.part_number || "—"}</td>
                          <td className="px-2 py-1 text-slate-800 font-medium truncate max-w-[180px]">{row.name}</td>
                          <td className="px-2 py-1 text-slate-500">{row.scope_name}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{row.on_hand}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{formatCurrency(row.part_cost)}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{row.labor_hours}</td>
                          <td className="px-2 py-1 text-slate-500">{row.manufacturer || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Import result */}
            {importResult && (
              <div className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm font-semibold text-slate-700">Import complete</span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div className="bg-emerald-50 rounded px-3 py-2 text-center">
                    <div className="text-2xl font-bold text-emerald-700 tabular-nums">{importResult.inserted}</div>
                    <div className="text-emerald-600">New parts</div>
                  </div>
                  <div className="bg-blue-50 rounded px-3 py-2 text-center">
                    <div className="text-2xl font-bold text-blue-700 tabular-nums">{importResult.updated}</div>
                    <div className="text-blue-600">Updated</div>
                  </div>
                  <div className="bg-slate-50 rounded px-3 py-2 text-center">
                    <div className="text-2xl font-bold text-slate-500 tabular-nums">{importResult.skipped}</div>
                    <div className="text-slate-500">Skipped</div>
                  </div>
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="space-y-1 mt-2">
                    <p className="text-xs font-semibold text-red-600">Errors ({importResult.errors.length}):</p>
                    {importResult.errors.map((e, i) => (
                      // eslint-disable-next-line react/no-array-index-key -- import error list; transient
                      <div key={`ie-${i}`} className="text-xs text-red-500 bg-red-50 rounded px-2 py-1 flex gap-2">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        Row {e.row}: {e.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between items-center pt-1">
              <div className="text-xs text-slate-400">
                {csvPreview.length > 0 && !importResult && `${csvPreview.length} rows ready`}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setCsvDialogOpen(false)}>Close</Button>
                {!importResult && (
                  <>
                    <Button variant="outline" disabled={!csvPreview.length || importing} onClick={() => handleCsvImport(false)} data-testid="btn-import-new-only">
                      Import New Only
                    </Button>
                    <Button disabled={!csvPreview.length || importing} onClick={() => handleCsvImport(true)} data-testid="btn-import-upsert">
                      {importing ? "Importing…" : "Import + Update Existing"}
                    </Button>
                  </>
                )}
                {importResult && (
                  <Button variant="outline" onClick={() => { setCsvText(""); setCsvPreview([]); setImportResult(null); }}>Import More</Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
